import { useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { ArrowRight, ArrowUp, Check, ChevronDown, RotateCw, Search, X, Zap } from 'lucide-react';
import { useAuth } from '../../Accounts/AuthContext';
import BuffaloAvatar from './BuffaloAvatar';
import BuffyMarkdown from './BuffyMarkdown';
import HomeQuestionViewer from './HomeQuestionViewer';
import Avatar from './Avatar';
import { Ring, DotStrip, Bar, Faces, Stat, ACCENT, QUIET } from './charts';
import { streamSandy } from '../data/sandyApi';
import { newConvoId } from '../data/sandyChatStore';
import { sendReminder, invalidateRoster } from '../data/api';
import { fetchHomeworkReport, reportEvidence, reportIsAssessment, reportSeed, pct, classAverage, rushSecondsOf } from '../data/homeworkReport';
import { dateLabel } from '../data/teacherHomeModel';
import './QuestionBreakout.css';
import './homeworkReport.css';

/* A practice set's report (Sandy 2026-10-13: "a little box with a shrunken
   down report … expand it to review the analysis … lives inside the
   assignments tab after, so they could revisit it or ask the chat again").
   The numbers come from getHomeworkReport (data/homeworkReport.js); Buffy's
   read on top is written from exactly those numbers. `compact` is the small
   version inside Home's brief; HomeworkReportModal is the full one. */

const nameOf = (report, id) => report.students?.find(s => s.userId === id)?.name || 'A student';

// Buffy's read: three sentences from the report data. Cached per report
// version for the session, so reopening never asks twice. assessment
// (2026-10-18): a curriculum assessment, read as one (same questions for
// everyone); left out, the report's own set decides.
const readCache = new Map();
export function BuffyRead({ report, compact = false, assessment: assessmentProp }) {
  const auth = useAuth();
  const key = `${report.hwId}:${report.fingerprint}`;
  // One transcript row per report read (audit F2): the read and its follow-ups.
  const convo = useRef({ key: null, id: null });
  if (convo.current.key !== key) convo.current = { key, id: newConvoId() };
  const assessment = assessmentProp ?? reportIsAssessment(report);
  const evidence = useMemo(() => reportEvidence(report, { assessment }), [report, assessment]);
  const [state, setState] = useState(() => readCache.has(key) ? { text: readCache.get(key), busy: false } : { text: '', busy: true });
  const [input, setInput] = useState('');
  const [followup, setFollowup] = useState('');
  const [attempt, setAttempt] = useState(0);
  useEffect(() => {
    if (auth?.viewAs) return undefined; // Buffy is off in view mode
    if (!followup && readCache.has(key) && !attempt) { setState({ text: readCache.get(key), busy: false }); return undefined; }
    const controller = new AbortController();
    let alive = true;
    let text = '';
    setState({ text: '', busy: true });
    const timer = setTimeout(() => controller.abort(), 45000);
    streamSandy({ auth, page: 'assignments', convoId: convo.current.id, actionsOk: false, signal: controller.signal, seed: reportSeed(assessment),
      messages: [{ role: 'user', content: `Report data: ${JSON.stringify(evidence)}`.slice(0, 12000) },
        { role: 'user', content: followup || 'Write the read for this report.' }],
      onDelta: d => { text += d; if (alive) setState({ text, busy: true }); },
      // The server switched models mid-reply (2026-10-18): drop the first
      // model's half sentence, or it stays glued in front of the real read.
      onReset: () => { text = ''; if (alive) setState({ text: '', busy: true }); },
    }).then(done => {
      if (!done || !text.trim()) throw new Error('incomplete');
      if (!alive) return;
      setState({ text, busy: false });
      if (!followup) readCache.set(key, text);
    }).catch(() => { if (alive) setState({ text: '', busy: false, error: true }); }).finally(() => clearTimeout(timer));
    return () => { alive = false; clearTimeout(timer); controller.abort(); };
  }, [key, followup, attempt]); // eslint-disable-line react-hooks/exhaustive-deps
  if (auth?.viewAs) return null;
  return <section className={`hr-read${compact ? ' is-compact' : ''}`} aria-label="Buffy’s read">
    <div className="hr-read-head"><BuffaloAvatar size={compact ? 32 : 40} mood={state.busy ? 'think' : null} /><span className="hr-read-label">Buffy’s read</span></div>
    {followup && <div className="hr-read-q">{followup}</div>}
    <div className="hr-read-text" aria-live="polite" aria-busy={state.busy}>
      {state.text ? <BuffyMarkdown streaming={state.busy}>{state.text}</BuffyMarkdown>
        : <p>{state.error ? 'I couldn’t write the read just now. The numbers below are complete.' : 'Reading the results…'}</p>}
    </div>
    {state.error && <button type="button" className="hr-link" onClick={() => setAttempt(n => n + 1)}>Try again</button>}
    {!compact && <form className="hr-ask" onSubmit={e => { e.preventDefault(); if (input.trim() && !state.busy) { setFollowup(input.trim()); setAttempt(n => n + 1); setInput(''); } }}>
      <input aria-label="Ask about this report" placeholder="Ask about this report…" value={input} maxLength={600} disabled={state.busy} onChange={e => setInput(e.target.value)} />
      <button type="submit" aria-label="Ask Buffy" disabled={state.busy || !input.trim()}><ArrowUp size={17} /></button>
    </form>}
    {followup && !compact && <button type="button" className="hr-link" onClick={() => { setFollowup(''); setAttempt(0); }}>Back to the read</button>}
  </section>;
}

// The weakest topic is the story: it alone wears the accent, and only when
// it's actually weak.
const weakestId = topics => {
  const w = [...topics].filter(t => t.accuracy != null).sort((a, b) => a.accuracy - b.accuracy)[0];
  return w && w.accuracy < 70 ? w.id : null;
};

// read: Buffy's read, placed right under the numbers in the full report.
export function ReportBody({ report, classId, auth, compact = false, actions = true, read = null }) {
  const [example, setExample] = useState(null);
  const [reminded, setReminded] = useState({});
  const [showStudents, setShowStudents] = useState(false);
  const [allTopics, setAllTopics] = useState(false);
  const [allQuestions, setAllQuestions] = useState(false);
  const topics = report.topics || [];
  // Groups that are only topics (no mistake-type labels on the questions)
  // repeat the topic bars, so they're left out.
  const mistakes = (report.mistakes || []).some(m => m.kind === 'trap') ? report.mistakes : [];
  const all = report.students || [];
  const students = [...all].sort((a, b) => (a.accuracy == null) - (b.accuracy == null) || (a.accuracy ?? 0) - (b.accuracy ?? 0) || String(a.name).localeCompare(String(b.name)));
  const avg = classAverage(all);
  const low = all.filter(s => s.accuracy != null && s.accuracy < 50).length;
  const weak = weakestId(topics);
  // Hardest first (the backend sorts by miss rate).
  const questions = report.questions || [];
  const topicLabel = id => topics.find(t => t.id === id)?.label || 'Question';
  const done = report.completion?.done ?? 0;
  const assigned = report.completion?.assigned ?? 0;
  const working = Math.max(0, (report.completion?.started ?? 0) - done);
  const points = all.map(s => ({ id: s.userId, label: s.name, value: s.accuracy }));
  const remind = async s => {
    setReminded(r => ({ ...r, [s.userId]: 'working' }));
    try { await sendReminder(auth, classId, s.userId); invalidateRoster(classId); setReminded(r => ({ ...r, [s.userId]: 'done' })); }
    catch { setReminded(r => ({ ...r, [s.userId]: 'error' })); }
  };
  const topicBars = list => list.map(t => <Bar key={t.id} label={t.label} value={t.accuracy} emphasis={t.id === weak}
    after={<Faces names={(t.strugglers || []).map(id => nameOf(report, id))} max={3} size={22} />} />);
  const mistakeRows = list => list.map(m => <div className="hr2-mistake" key={m.key}>
    <span className="hr2-count">{m.count}</span>
    <span className="hr2-mistake-name">{m.label}</span>
    <Faces names={(m.students || []).map(id => nameOf(report, id))} max={3} size={22} />
    {m.examples?.length > 0 && <button type="button" className="hr2-icon" aria-label={`See an example: ${m.label}`} title="See an example" onClick={() => setExample(m)}><Search size={15} /></button>}
  </div>);
  const viewer = example && <HomeQuestionViewer auth={auth} classId={classId} questions={example.examples.map(x => ({ record: x.recordID, label: example.label }))} text={`${example.label}: ${example.count} wrong answers.`} onClose={() => setExample(null)} />;

  if (compact) return <div className="hr2-compact">
    {avg != null && <div className="hr2-compact-top"><Stat value={pct(avg)} label="average" /><DotStrip points={points} average={avg} named={1} /></div>}
    {topics.length > 0 && <div className="hr2-block">{topicBars(topics.slice(0, 3))}</div>}
    {mistakes.length > 0 && <div className="hr2-block">{mistakeRows(mistakes.slice(0, 1))}</div>}
    {viewer}
  </div>;

  return <div className="hr2">
    <div className="hr2-kpis">
      <Stat value={pct(avg)} label="Class average" />
      <div className="hr2-ringstat"><Ring done={done} working={working} total={assigned} size={52} stroke={7} /><Stat value={`${done}/${assigned}`} label="Finished" /></div>
      <Stat value={low} label={low === 1 ? 'Student under 50%' : 'Students under 50%'} />
    </div>
    {read}
    {avg != null && <section className="hr2-section" aria-label="Scores"><div className="hr-h">Scores</div><DotStrip points={points} average={avg} /></section>}
    <div className={mistakes.length ? 'hr2-cols' : 'hr2-bars'}>
      <section className="hr2-section" aria-label="By topic"><div className="hr-h">By topic</div>
        {topics.length ? topicBars(allTopics ? topics : topics.slice(0, 5)) : <div className="hr-empty">No answers yet.</div>}
        {topics.length > 5 && <button type="button" className="hr-link" aria-expanded={allTopics} onClick={() => setAllTopics(v => !v)}>{allTopics ? 'Fewer topics' : `All ${topics.length} topics`}</button>}</section>
      {mistakes.length > 0 && <section className="hr2-section" aria-label="Common mistakes"><div className="hr-h">Common mistakes</div>{mistakeRows(mistakes)}</section>}
    </div>
    {questions.length > 0 && <section className="hr2-section hr2-bars" aria-label="Hardest questions">
      <div className="hr-h">Hardest questions</div>
      {(allQuestions ? questions : questions.slice(0, 5)).map(q => {
        const about = `${q.seenBy} saw it${q.commonWrong ? `, most picked ${q.commonWrong.letter}` : ''}`;
        return <Bar key={q.recordID} label={topicLabel(q.topicId)} value={100 - q.missRate} emphasis={q.missRate >= 50}
          after={<button type="button" className="hr2-icon" aria-label={`See this question (${about})`} title={about} onClick={() => setExample({ label: topicLabel(q.topicId), count: q.missed, examples: [{ recordID: q.recordID }] })}><Search size={15} /></button>} />;
      })}
      {questions.length > 5 && <button type="button" className="hr-link" aria-expanded={allQuestions} onClick={() => setAllQuestions(v => !v)}>{allQuestions ? 'Fewer questions' : `All ${questions.length} shared questions`}</button>}
    </section>}
    <section className="hr2-section" aria-label="Students">
      <button type="button" className="hr2-disclose" aria-expanded={showStudents} onClick={() => setShowStudents(v => !v)}>
        <span className="hr-h">Students</span><span className="hr2-disclose-n">{all.length}</span><ChevronDown size={16} aria-hidden="true" /></button>
      {showStudents && <div role="list" className="hr2-students">{students.map(s => <div role="listitem" key={s.userId}>
        <Avatar name={s.name} size={26} />
        <span className="hr2-st-name">{s.name}{s.rushed > 0 && <span className="hr-flag" title={`${s.rushed} answers in under ${rushSecondsOf(report)} seconds`}><Zap size={11} aria-hidden="true" />{s.rushed}</span>}</span>
        {s.status === 'notStarted'
          ? <span className="hr2-st-none">{!actions ? 'Not started' : reminded[s.userId] === 'done' ? <span className="hr-done"><Check size={13} aria-hidden="true" />Reminded</span>
            : <button type="button" className="hr-chip" disabled={reminded[s.userId] === 'working'} onClick={() => remind(s)}>{reminded[s.userId] === 'error' ? 'Try again' : 'Not started · Remind'}</button>}</span>
          : <span className="ch-bar-track hr2-st-bar" aria-hidden="true"><i style={{ width: `${Math.max(2, s.accuracy || 0)}%`, background: s.accuracy != null && s.accuracy < 50 ? ACCENT : QUIET }} /></span>}
        <span className="hr2-st-pct">{s.status === 'notStarted' ? '' : pct(s.accuracy)}</span>
      </div>)}</div>}
    </section>
    {viewer}
  </div>;
}

// The full report, from the Assignments tab or "Open full report" in the brief.
// assessment (2026-10-18): the page knows from the set's listHomework row; the
// report's own set says so too once it loads.
export function HomeworkReportModal({ auth, classId, hwId, title, actions = true, assessment: assessmentProp = false, onClose }) {
  const [state, setState] = useState({ loading: true });
  const [refreshing, setRefreshing] = useState(false);
  const root = useRef(null);
  const load = (refresh = false) => {
    const controller = new AbortController();
    if (refresh) setRefreshing(true); else setState({ loading: true });
    fetchHomeworkReport(auth, classId, hwId, { refresh, signal: controller.signal })
      .then(report => setState({ report }))
      .catch(err => { if (!controller.signal.aborted) setState(s => (refresh && s.report ? s : { error: err.code === 'REPORT_UNAVAILABLE' ? 'Reports aren’t turned on yet. Gradebook has each student’s results.' : 'I couldn’t load this report. Try again in a moment.' })); })
      .finally(() => setRefreshing(false));
    return controller;
  };
  useEffect(() => { const c = load(); return () => c.abort(); }, [hwId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    const key = e => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', key);
    root.current?.querySelector('button')?.focus();
    return () => document.removeEventListener('keydown', key);
  }, [onClose]);
  const r = state.report;
  const assessment = !!assessmentProp || reportIsAssessment(r);
  const when = r ? (r.final ? `Finished${r.set?.closedAt || r.set?.dueDate ? ` ${dateLabel(r.set.closedAt || r.set.dueDate)}` : ''}` : 'So far') : '';
  return createPortal(<div ref={root} className="buffy-question-dialog fixed inset-0 flex items-center justify-center font-sans antialiased text-ink" style={{ zIndex: 1100, padding: 'clamp(12px, 3vw, 20px)' }} role="dialog" aria-modal="true" aria-label={`${title || r?.set?.title || 'Homework'} report`}>
    <div className="absolute inset-0 buffy-question-backdrop" onClick={onClose} />
    <div className="buffy-question-panel relative w-[1120px] max-w-[96vw] h-[860px] max-h-[92vh] flex flex-col overflow-hidden">
      <div className="buffy-question-header hr-head flex items-center gap-3 flex-none">
        <div className="hr-title flex-1 min-w-0">
          <div className="buffy-question-title">{r?.set?.title || title || 'Homework report'}</div>
          <div className="hr-sub">{r ? `${when} · ${r.completion?.done ?? 0} of ${r.completion?.assigned ?? 0} done${assessment ? ' · same questions for everyone' : r.sharedQuestions ? '' : ' · each student had their own questions'}` : 'Report'}</div>
        </div>
        <span className="hr-head-actions">
          {r && <button type="button" className="hr-chip" disabled={refreshing} onClick={() => load(true)}><RotateCw size={14} aria-hidden="true" />{refreshing ? 'Updating…' : 'Update'}</button>}
          <Link className="hr-chip" to={`/teacher-dashboard/gradebook?homework=${encodeURIComponent(hwId)}`} onClick={onClose}>Gradebook<ArrowRight size={14} aria-hidden="true" /></Link>
        </span>
        <button type="button" onClick={onClose} aria-label="Close" className="hr-close w-8 h-8 rounded-full flex items-center justify-center bg-neutral-100 hover:bg-neutral-200 text-neutral-500 hover:text-ink border-0 cursor-pointer flex-none"><X size={15} strokeWidth={2.5} /></button>
      </div>
      <div className="hr-scroll">
        {state.loading && <div className="hr-loading" role="status"><span className="buffy-chat-dots" aria-hidden="true"><i /><i /><i /></span>Putting the report together…</div>}
        {state.error && <p className="hr-error" role="alert">{state.error}</p>}
        {r && <ReportBody report={r} classId={classId} auth={auth} actions={actions} read={<BuffyRead report={r} assessment={assessment} />} />}
      </div>
    </div>
  </div>, document.body);
}
